import { FC } from "react";
import { useNavigate, useParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/button";
import { api } from "@/modules/api";
import { Uuid } from "@/domains/global/identifiers";
import { atlasRoutes } from "@/app/router/routes";
import { getFileDownloadUrl, isImageMimeType, formatFileSize } from "../utils";
import { Box, Card, Heading, Text, Flex } from "@radix-ui/themes";

export const UploadedFilesViewPage: FC = () => {
  const { uploadedFileId } = useParams<{ uploadedFileId: Uuid }>();
  const navigate = useNavigate();

  if (!uploadedFileId) return <div>Invalid ID</div>;

  const result = useQuery({
    queryKey: ["uploaded-file", uploadedFileId],
    queryFn: () =>
      api.get("/internal/v1/uploaded-files/{uploaded_file_id}", {
        params: { path: { uploaded_file_id: uploadedFileId } },
      }),
  });

  const handleDelete = async () => {
    await api.delete("/internal/v1/uploaded-files/{uploaded_file_id}", {
      params: { path: { uploaded_file_id: uploadedFileId } },
    });
    navigate(atlasRoutes.uploadedFiles({}).path);
  };

  if (result.isLoading) return <div>Loading...</div>;
  if (result.error) return <div>Error loading file</div>;
  if (!result.data?.data) return <div>File not found</div>;

  const file = result.data.data;
  const downloadUrl = getFileDownloadUrl(
    file.uploaded_file_bucket_name,
    file.uploaded_file_blob_name
  );
  const isImage = isImageMimeType(file.mime_type);

  return (
    <Box p="4">
      <Heading size="6" mb="4">
        View Uploaded File
      </Heading>

      <Card mb="4">
        <Flex direction="column" gap="3">
          <Box>
            <Text size="2" color="gray">
              File ID:
            </Text>
            <Text
              size="3"
              weight="bold"
              style={{
                fontFamily: "monospace",
                userSelect: "all",
                paddingLeft: "8px",
                fontStyle: "italic",
              }}
            >
              {file.id}
            </Text>
          </Box>
          <Box>
            <Text size="2" color="gray">
              Filename:
            </Text>
            <Text size="3" weight="bold">
              {" "}
              {file.normalized_filename}
            </Text>
          </Box>
          <Box>
            <Text size="2" color="gray">
              MIME Type:
            </Text>
            <Text size="3"> {file.mime_type}</Text>
          </Box>
          <Box>
            <Text size="2" color="gray">
              Size:
            </Text>
            <Text size="3"> {formatFileSize(file.file_size_bytes)}</Text>
          </Box>
          <Box>
            <Text size="2" color="gray">
              Created:
            </Text>
            <Text size="3"> {new Date(file.created_at).toLocaleString()}</Text>
          </Box>
          <Box>
            <Text size="2" color="gray">
              Owner Space Account ID:
            </Text>
            <Text size="3"> {file.owner_space_account_id}</Text>
          </Box>
        </Flex>
      </Card>

      {isImage && (
        <Card mb="4">
          <Flex direction="column" gap="2">
            <Text size="2" color="gray">
              Preview:
            </Text>
            <Box>
              <img
                src={downloadUrl}
                alt={file.normalized_filename}
                style={{
                  maxWidth: "300px",
                  maxHeight: "300px",
                  objectFit: "contain",
                  borderRadius: "4px",
                  border: "1px solid var(--gray-a6)",
                  display: "block",
                }}
              />
            </Box>
          </Flex>
        </Card>
      )}

      <Flex gap="3">
        <Button size="2" variant="soft" asChild>
          <a href={downloadUrl} target="_blank" rel="noopener noreferrer">
            Open in New Tab
          </a>
        </Button>
        <Button size="2" variant="soft" asChild>
          <a href={downloadUrl} download={file.normalized_filename}>
            Download
          </a>
        </Button>
        <Button size="2" color="red" onClick={handleDelete}>
          Delete
        </Button>
      </Flex>

      <details style={{ marginTop: "32px" }}>
        <summary style={{ cursor: "pointer", marginBottom: "8px" }}>
          <Text size="2" color="gray">
            Raw JSON Data
          </Text>
        </summary>
        <pre
          style={{
            fontSize: "12px",
            background: "var(--gray-a3)",
            padding: "12px",
            borderRadius: "4px",
            overflow: "auto",
          }}
        >
          {JSON.stringify(file, null, 2)}
        </pre>
      </details>
    </Box>
  );
};
